import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Box } from '@wix/design-system';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useTemplatesQuery } from '@components/Hooks/useTemplatesQuery';
import {
  useTemplatesContext,
  ACTIONS,
} from '@components/Contexts/TemplatesContext';
import {
  INITIAL_TEMPLATES_LOAD_SIZE,
  MAX_TEMPLATES_COUNT_FOR_THREE_IN_ROW,
  TEMPLATES_DISPLAY_PAGE_SIZE,
} from '@utils/profileTemplatesUtils';
import { TemplateGalleryCard } from '@common/types';
import ProfileTemplatesHeader from './ProfileTemplatesHeader';
import ProfileTemplateGalleries from './ProfileTemplateGalleries';
import LoadingTemplateGallery from './LoadingTemplateGallery';

interface TemplatesProps {
  isMobileView: boolean;
  onShowLess?: () => void;
}

const ProfileTemplates: React.FC<TemplatesProps> = ({
  isMobileView,
  onShowLess,
}) => {
  const templatesRef = useRef<HTMLDivElement>(null);
  const { partnerProfile } = useProfileContext();
  const {
    state: {
      filters,
      numberOfResults: numberOfTemplates,
      templatesAverageScore,
    },
    dispatch,
  } = useTemplatesContext();

  const [loadedTemplates, setLoadedTemplates] = useState<TemplateGalleryCard[]>(
    [],
  );
  const [queryConfig, setQueryConfig] = useState({
    page: 0,
    pageSize: INITIAL_TEMPLATES_LOAD_SIZE,
  });
  const [visibleTemplatesCount, setVisibleTemplatesCount] = useState(0);

  const handleVisibleCountChange = useCallback(
    (count: number) => {
      setVisibleTemplatesCount(count);
      dispatch({
        type: ACTIONS.SET_VISIBLE_TEMPLATES_COUNT,
        visibleTemplatesCount: count,
      });
    },
    [dispatch],
  );

  const { data, isLoading } = useTemplatesQuery(
    {
      ...filters,
      partnerId: partnerProfile.id || '',
    },
    queryConfig.page,
    'relevance',
    queryConfig.pageSize,
  );

  useEffect(() => {
    if (data?.templates) {
      setLoadedTemplates((prev) => [...prev, ...data.templates]);
    }
  }, [data?.templates, queryConfig]);

  const triggerFetchMoreTemplates = useCallback(() => {
    if (loadedTemplates.length < numberOfTemplates) {
      const nextPage =
        Math.floor(loadedTemplates.length / TEMPLATES_DISPLAY_PAGE_SIZE) + 1;
      setQueryConfig({
        page: nextPage,
        pageSize: TEMPLATES_DISPLAY_PAGE_SIZE,
      });
    }
  }, [loadedTemplates.length, numberOfTemplates]);

  if (isLoading && loadedTemplates.length === 0) {
    return (
      <LoadingTemplateGallery
        isMobileView={isMobileView}
        shouldShowThreeInRow={
          !isMobileView &&
          numberOfTemplates <= MAX_TEMPLATES_COUNT_FOR_THREE_IN_ROW
        }
      />
    );
  }

  return (
    <Box
      direction="vertical"
      ref={templatesRef}
      padding={isMobileView ? '0 6px' : '0'}
      gap={isMobileView ? '24px' : '0'}
    >
      <ProfileTemplatesHeader
        isMobileView={isMobileView}
        totalTemplatesLength={numberOfTemplates}
        averageRating={templatesAverageScore}
        loadedTemplatesLength={visibleTemplatesCount}
      />

      <ProfileTemplateGalleries
        isMobileView={isMobileView}
        templatesRef={templatesRef}
        templates={loadedTemplates}
        onNeedMoreTemplates={triggerFetchMoreTemplates}
        totalAvailable={numberOfTemplates}
        onVisibleCountChange={handleVisibleCountChange}
        onShowLess={onShowLess}
      />
    </Box>
  );
};

export default ProfileTemplates;
